/**
 * Paystack helpers for the customer display: Mobile Money networks and numbers, the card
 * checkout popup and QR codes. Payments themselves are started by the till or the server;
 * the display only shows them.
 */
import qrcode from "qrcode-generator";

export type IntentStatus = "pending" | "send_otp" | "pay_offline" | "success" | "failed" | "abandoned" | "reversed";
export type Intent = {
  id: string;
  reference: string;
  saleId: string;
  method: "momo" | "card" | "any";
  channel: "screen" | "qr" | "link" | "prompt";
  amount: number;
  phone: string | null;
  provider: string | null;
  status: IntentStatus;
  message: string | null;
  accessCode: string | null;
  authorizationUrl: string | null;
  paidWith: string | null;
  createdAt: number;
};
export type Provider = "mtn" | "vod" | "atl";
export const PROVIDERS: { id: Provider; label: string }[] = [
  { id: "mtn", label: "MTN" },
  { id: "vod", label: "Telecel" },
  { id: "atl", label: "AirtelTigo" },
];
export const providerLabel = (p: string | null | undefined) => PROVIDERS.find((x) => x.id === p)?.label ?? "";

/**
 * Ghana numbers as typed, either way: 024 123 4567 (10 digits) or 233 24 123 4567 / +233… (12).
 * `keep` limits what can be typed; `local` is the 0XXXXXXXXX form; `ok` when it's a whole number.
 */
export function ghPhone(raw: string): { keep: string; local: string; ok: boolean } {
  let d = raw.replace(/\D/g, "");
  d = d.startsWith("233") ? d.slice(0, 12) : d.slice(0, 10);
  const local = d.startsWith("233") ? (d.length > 3 ? `0${d.slice(3)}` : "") : d;
  return { keep: d, local, ok: /^0\d{9}$/.test(local) };
}
/** The hint shown under a number that isn't complete yet. */
export const PHONE_HINT = "10 digits starting with 0 (024 123 4567), or 12 starting with 233";

/** Ghana numbers tell their network by the first three digits; null when it can't be told yet. */
export function guessProvider(phone: string): Provider | null {
  const p = phone.replace(/\D/g, "").replace(/^233/, "0").slice(0, 3);
  if (["024", "025", "053", "054", "055", "059"].includes(p)) return "mtn";
  if (["020", "050"].includes(p)) return "vod";
  if (["026", "027", "056", "057"].includes(p)) return "atl";
  return null;
}


export const isOpen = (s: IntentStatus) => s === "pending" || s === "send_otp" || s === "pay_offline";

/** "024 555 0182" while typing. */
export const fmtPhone = (d: string) => (d.length <= 3 ? d : d.length <= 6 ? `${d.slice(0, 3)} ${d.slice(3)}` : `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 10)}`);

// ─────────────────────────── Paystack's on-screen checkout (InlineJS v2) ───────────────────────────
type PaystackPop = { resumeTransaction: (accessCode: string, cb?: { onSuccess?: (t: unknown) => void; onCancel?: () => void; onError?: (e: unknown) => void }) => void };
let loading: Promise<new () => PaystackPop> | null = null;
function loadInline(): Promise<new () => PaystackPop> {
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const w = window as unknown as { PaystackPop?: new () => PaystackPop };
      if (w.PaystackPop) return resolve(w.PaystackPop);
      const sc = document.createElement("script");
      sc.src = "https://js.paystack.co/v2/inline.js";
      sc.async = true;
      sc.onload = () => (w.PaystackPop ? resolve(w.PaystackPop) : reject(new Error("Paystack didn't load")));
      sc.onerror = () => {
        loading = null;
        reject(new Error("Couldn't reach Paystack. Check the internet"));
      };
      document.head.appendChild(sc);
    });
  }
  return loading;
}

/** Open Paystack's own secure checkout for a started transaction. Resolves when it closes. */
export async function openCheckout(accessCode: string): Promise<"success" | "closed"> {
  const Pop = await loadInline();
  return new Promise((resolve) => {
    new Pop().resumeTransaction(accessCode, {
      onSuccess: () => resolve("success"),
      onCancel: () => resolve("closed"),
      onError: () => resolve("closed"),
    });
  });
}

// ─────────────────────────── QR ───────────────────────────
/** Square modules of a QR code for `text` (true = dark), for drawing as SVG. */
export function qrModules(text: string): boolean[][] {
  const q = qrcode(0, "M");
  q.addData(text);
  q.make();
  const n = q.getModuleCount();
  return Array.from({ length: n }, (_, r) => Array.from({ length: n }, (_, c) => q.isDark(r, c)));
}
